/**
 * THE WIZARD — what is the ONE next thing, and is this survey finishable yet?
 *
 * From the design authority, 2026-08-06:
 *
 *   "Our users are senior surveyors, around 60 years old. The form must guide
 *    them through the entire process without requiring them to click around or
 *    figure out confusing navigation. It should be as simple as QuickBooks,
 *    indicating 'Starting Machine Room 1' and then 'Now let's tackle Machine
 *    Room 2'."
 *
 * and, on the close button:
 *
 *   "There is a 'Close and Submit' button which could easily be clicked by
 *    mistake. Users should not be able to close and submit until all sections
 *    are complete." … "The survey form currently allows users to close and
 *    submit before the second machine room has been surveyed."
 *
 * This module answers both, in one place, so the section screen, the hall
 * indicator and the close screen can never disagree about where the surveyor
 * is or whether they are done.
 *
 * ── WALKED is not COMPLETE, and the difference is the whole design ──────────
 * The gate is "has the surveyor been through every machine room", NOT "is every
 * answer perfect". Spec §2.3 is explicit that a survey with genuine gaps — a
 * pit that was locked out — still submits, and §4 rule 8 says a missing photo
 * never blocks. Nothing here inspects an answer for correctness and nothing
 * here removes, adds or re-decides a gap: `detectGaps` and `validate` still own
 * all of that, and the close screen still lists every gap and sends it.
 *
 * What this stops is "I didn't know there was a room 2". So a section counts as
 * walked when the surveyor has been shown it (`walked.ts`) **or** when the
 * engines already report activity in it, and a section that asks nothing counts
 * automatically — a screen with no questions cannot be "not done".
 *
 * ── Progressive disclosure, not a disabled button ───────────────────────────
 * `ready` is false → the screens show `step` instead of Submit. They never show
 * a greyed-out Submit with a tooltip: a disabled control is a puzzle, an absent
 * one is calm. Every step carries the sentence to print AND the button label,
 * so no screen has to compose the wizard's voice itself.
 *
 * Pure: no React, no store, no clock. `tests/unit/guided-flow.test.ts` runs it
 * directly.
 */

import { asksCatalogQuestions, isSurveyLevel } from '../../core/sections';
import type { MachineRoom, SectionInstance } from '../../core/types';
import type { Route } from '../routing';
import { expectedRoomCount } from './buildingSetup';
import type { SurveyResults } from './results';

/* ─────────────────────────────── the pieces ─────────────────────────────── */

export type GuideStepKind = 'section' | 'roomSetup' | 'addRoom' | 'submit';

export interface GuideStep {
  kind: GuideStepKind;
  /** Where the primary button goes. */
  route: Route;
  /**
   * The wizard's own sentence — a full sentence telling the surveyor what
   * happens next, in the words the authority asked for. Never a state label.
   */
  sentence: string;
  /** What the button says. Short enough for a 430px screen. */
  action: string;
  /** The machine room this step belongs to, when it belongs to one. */
  roomId?: string;
}

export interface GuideRoom {
  roomId: string;
  name: string;
  /** 1-based, among the machine rooms that exist on the document. */
  position: number;
  /** Cars, groups and hoistways all defined — the hinge the rest is built from. */
  setupReady: boolean;
  sections: SectionInstance[];
  /** Sections of this room the surveyor has not been shown yet, in walk order. */
  remaining: SectionInstance[];
  walked: boolean;
}

export interface GuidePlan {
  rooms: GuideRoom[];
  /** B12's count, or the number of rooms that exist — whichever is larger. */
  totalRooms: number;
  /** Machine rooms the building says exist that have no survey started. */
  pendingRooms: number;
  /** True when Close & submit may be shown at all. */
  ready: boolean;
  /** Every room still owing a walk. What the close screen lists when not ready. */
  outstanding: GuideRoom[];
  /**
   * The survey-level catalog sections still owing a walk — Fire Life Safety, in
   * practice (Amendment 6, 2026-08-18). Building is one too, but a survey cannot
   * have a room without its building screen having been opened, so it is
   * walked by the time anything else is. Ad Hoc asks nothing and is never here.
   */
  outstandingSurveyLevel: SectionInstance[];
  /** The ONE next action. Always present — when ready, it is submit. */
  step: GuideStep;
}

/* ────────────────────────────── walked-ness ─────────────────────────────── */

/**
 * Has the surveyor been through this section?
 *
 * Three ways to be true, and the order matters:
 *
 *   1. the device recorded showing it (`walked.ts`)
 *   2. the engines report activity — anything but `todo`. (A surveyor-flagged
 *      section used to count here; since D-033 a section nobody could get
 *      into is answered No / 0 with a note, which is activity like any other.)
 *   3. it asks nothing at all — a section with no visible questions can never
 *      leave `todo`, so without this clause it would block a survey forever
 */
export function isWalked(
  results: SurveyResults,
  section: SectionInstance,
  walked: ReadonlySet<string>,
): boolean {
  if (walked.has(section.id)) return true;
  const progress = results.progressBySection.get(section.id);
  if (!progress) return false;
  if (progress.total === 0) return true;
  return progress.status !== 'todo';
}

/**
 * The structure a room's survey is generated from (brief §5).
 *
 * Read off the entity tree, which is a fact rather than a derivation — the same
 * read `results.ts` makes when it decides whether room setup can be `done`. A
 * room with no hoistways generates no Pit and no Overhead, so "every section
 * walked" would be trivially true of a room nobody has set up.
 */
function setupReadyFor(room: MachineRoom): boolean {
  return room.cars.length > 0 && room.groups.length > 0 && room.hoistways.length > 0;
}

/* ─────────────────────────────── the plan ───────────────────────────────── */

function ordinalRoom(position: number, total: number): string {
  return total > 1 ? `machine room ${position} of ${total}` : 'this machine room';
}

/**
 * The whole guided plan for a survey.
 *
 * `walked` comes from `useWalkedSections(surveyId)`; pass an empty set to ask
 * "what does the document alone say?".
 */
export function guidePlan(results: SurveyResults, walked: ReadonlySet<string>): GuidePlan {
  const doc = results.doc;
  const surveyId = doc.surveyId;

  const rooms: GuideRoom[] = doc.rooms.map((room, index) => {
    const sections = results.sections.filter((section) => section.scope.roomId === room.id);
    const remaining = sections.filter((section) => !isWalked(results, section, walked));
    const setupReady = setupReadyFor(room);
    return {
      roomId: room.id,
      name: room.name.trim() || `Machine room ${String(index + 1)}`,
      position: index + 1,
      setupReady,
      sections,
      remaining,
      walked: setupReady && sections.length > 0 && remaining.length === 0,
    };
  });

  const declared = expectedRoomCount(doc) ?? 0;
  const totalRooms = Math.max(declared, rooms.length);
  const pendingRooms = Math.max(0, declared - rooms.length);
  const outstanding = rooms.filter((room) => !room.walked);

  /*
   * ── The sections that belong to the whole job — Chip, 2026-08-18 ────────────
   *   *"Place it among the final sections of the survey, ideally the last, so
   *    the Surveyor can close and submit only after completing it."*
   * The gate used to count ROOMS only, so a survey-level section could be walked
   * past without anyone noticing — which is exactly the "I didn't know there was
   * a room 2" defect this module exists to stop, in a new costume. Now every
   * survey-level section that asks catalog questions must be walked too. Walked,
   * not complete: F2–F5 are not required (spec §3.11) and photos never block
   * (§4 rule 8) — opening the section, or answering into it, is enough.
   */
  const outstandingSurveyLevel = results.sections.filter(
    (section) =>
      isSurveyLevel(section.kind) &&
      asksCatalogQuestions(section.kind) &&
      !isWalked(results, section, walked),
  );

  const ready =
    rooms.length > 0 &&
    outstanding.length === 0 &&
    pendingRooms === 0 &&
    outstandingSurveyLevel.length === 0;

  return {
    rooms,
    totalRooms,
    pendingRooms,
    ready,
    outstanding,
    outstandingSurveyLevel,
    step: nextStep({ surveyId, rooms, totalRooms, pendingRooms, outstandingSurveyLevel, ready }),
  };
}

interface StepInput {
  surveyId: string;
  rooms: GuideRoom[];
  totalRooms: number;
  pendingRooms: number;
  outstandingSurveyLevel: SectionInstance[];
  ready: boolean;
}

/**
 * The one obvious next action.
 *
 * Walked in the order the surveyor works: set a room up, walk its sections,
 * move to the next room, then close. The first thing that is not finished wins;
 * nothing else is offered, and nothing not yet applicable is named.
 */
function nextStep({
  surveyId,
  rooms,
  totalRooms,
  pendingRooms,
  outstandingSurveyLevel,
  ready,
}: StepInput): GuideStep {
  if (ready) {
    return {
      kind: 'submit',
      route: { name: 'submit', surveyId },
      sentence:
        totalRooms > 1
          ? `All ${String(totalRooms)} machine rooms are walked. You can close this survey and send it.`
          : 'This machine room is walked. You can close this survey and send it.',
      action: 'Close & submit',
    };
  }

  for (const room of rooms) {
    if (!room.setupReady) {
      return {
        kind: 'roomSetup',
        route: { name: 'roomSetup', surveyId, roomId: room.roomId },
        sentence: `${boundaryLead(rooms, room.position)}Let's set up ${room.name} — its cars, groups and hoistways.`,
        action: `Set up ${room.name}`,
        roomId: room.roomId,
      };
    }
    const next = room.remaining[0];
    if (next) {
      return {
        kind: 'section',
        route: { name: 'section', surveyId, sectionId: next.id },
        sentence: `${boundaryLead(rooms, room.position)}Next in ${room.name}: ${next.title}.`,
        action: `Next: ${next.title}`,
        roomId: room.roomId,
      };
    }
  }

  // Every room that EXISTS is walked, and the building says there are more.
  if (rooms.length === 0 || pendingRooms > 0) {
    const number = rooms.length + 1;
    const previous = rooms[rooms.length - 1];
    return {
      kind: 'addRoom',
      route: { name: 'building', surveyId },
      sentence: previous
        ? `${previous.name} is done. Now let's tackle ${ordinalRoom(number, totalRooms)}.`
        : `Let's start ${ordinalRoom(number, totalRooms)}.`,
      // D-029 R7: the button that CREATES the room, wherever it renders. One
      // name — the sentence above it says which room the survey is on.
      action: 'Start Survey',
    };
  }

  // Every machine room is walked. What is left belongs to the whole job —
  // Fire Life Safety, the panel on the way out (Amendment 6). Said as the
  // handoff it is: the rooms are done, this is the last thing.
  const last = outstandingSurveyLevel[0];
  if (last) {
    return {
      kind: 'section',
      route: { name: 'section', surveyId, sectionId: last.id },
      sentence: `${allRoomsLead(rooms, totalRooms)}Last thing: ${last.title}${surveyLevelGloss(last)}.`,
      action: `Next: ${last.title}`,
    };
  }

  // Unreachable in practice — `ready` is exactly the negation of everything
  // above — but a wizard that can return nothing is a dead end by construction.
  return {
    kind: 'submit',
    route: { name: 'submit', surveyId },
    sentence: 'You can close this survey and send it.',
    action: 'Close & submit',
  };
}

/** "Every machine room is walked. " — or, on a one-room job, the room by name. */
function allRoomsLead(rooms: readonly GuideRoom[], totalRooms: number): string {
  if (totalRooms > 1) return 'Every machine room is walked. ';
  const only = rooms[0];
  return only ? `${only.name} is walked. ` : '';
}

/** What a survey-level section IS, for a surveyor who has not seen it yet. */
function surveyLevelGloss(section: SectionInstance): string {
  return section.kind === 'fls' ? ' — the fire alarm panel and its sticker' : '';
}

/**
 * "Penthouse Machine Room is done. " — the QuickBooks handoff, or nothing.
 *
 * Only spoken at a real boundary: the room BEFORE the one being pointed at has
 * to exist and be finished. Said on every step it would be noise, and said when
 * the previous room is half-finished it would be a lie.
 */
function boundaryLead(rooms: readonly GuideRoom[], position: number): string {
  const previous = rooms[position - 2];
  if (!previous?.walked) return '';
  return `${previous.name} is done. `;
}

/* ───────────────────────── per-room and per-section views ───────────────── */

/**
 * The next action WITHIN one machine room — what the hall indicator offers.
 *
 * A surveyor standing on room 2's indicator must not be sent back to room 1 by
 * a survey-wide "next": the panel they are looking at is about this room. Once
 * this room is finished the survey-wide step takes over, which is what carries
 * them across the boundary to room 2 or to submit.
 */
export function roomStep(plan: GuidePlan, roomId: string, surveyId: string): GuideStep {
  const room = plan.rooms.find((candidate) => candidate.roomId === roomId);
  const next = room?.remaining[0];
  if (room && next) {
    return {
      kind: 'section',
      route: { name: 'section', surveyId, sectionId: next.id },
      sentence: `Next in ${room.name}: ${next.title}.`,
      action: `Next: ${next.title}`,
      roomId,
    };
  }
  return plan.step;
}

/** "Machine room 1 of 2 · Penthouse Machine Room" — where you are, in words. */
export function whereYouAre(plan: GuidePlan, roomId: string | undefined): string | undefined {
  const room = plan.rooms.find((candidate) => candidate.roomId === roomId);
  if (!room) return undefined;
  if (plan.totalRooms <= 1) return room.name;
  return `Machine room ${String(room.position)} of ${String(plan.totalRooms)} · ${room.name}`;
}

/**
 * What the close screen says when the survey is not finishable yet.
 *
 * Deliberately not red and deliberately not called an error: the surveyor has
 * not done anything wrong, there is simply more job. Photos and gaps are not
 * mentioned — neither of them has ever blocked anything and naming them here
 * would teach the opposite.
 */
export function notReadySummary(plan: GuidePlan): string {
  if (plan.rooms.length === 0) {
    return 'This survey has no machine rooms yet. Set the first one up and the form will build the rest from it.';
  }
  const rooms = plan.outstanding.length;
  const pending = plan.pendingRooms;
  if (rooms > 0 && pending > 0) {
    return `${plural(rooms, 'machine room')} still to walk, and ${plural(pending, 'more machine room')} not started yet.`;
  }
  if (pending > 0) {
    return `The building says there ${pending === 1 ? 'is' : 'are'} ${plural(pending, 'more machine room')} to survey.`;
  }
  if (rooms > 0) return `${plural(rooms, 'machine room')} still to walk through.`;
  // Every room is walked; what is left belongs to the whole job (Amendment 6).
  // Named, never "0 machine rooms still to walk" — that sentence would be true
  // and would explain nothing.
  const last = plan.outstandingSurveyLevel[0];
  if (last) {
    return `Every machine room is walked. ${last.title} is the last section${surveyLevelGloss(last)} — and it has not been opened yet.`;
  }
  return 'Everything is walked. You can close this survey and send it.';
}


function plural(count: number, one: string, many = `${one}s`): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/* ───────────────────────────── the submitted record ─────────────────────── */

export interface SubmittedFacts {
  rooms: number;
  sections: number;
  photos: number;
  gaps: number;
  submittedAt?: string;
}

/**
 * What a submitted survey shows when the surveyor opens it again (feedback c —
 * "once Close and Submit is pressed the survey becomes inaccessible").
 *
 * Facts only, read straight off the sealed document. The survey-level sections
 * — Building, Fire Life Safety, Ad Hoc — are not counted as sections walked in
 * a machine room; the count is the rooms' own.
 */
export function submittedFacts(results: SurveyResults): SubmittedFacts {
  const doc = results.doc;
  const facts: SubmittedFacts = {
    rooms: doc.rooms.length,
    sections: results.sections.filter((section) => !isSurveyLevel(section.kind)).length,
    photos: doc.photos.length,
    gaps: doc.gaps.length,
  };
  if (doc.submittedAt) facts.submittedAt = doc.submittedAt;
  return facts;
}
